import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { MatIcon } from "@angular/material/icon";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { ActionService } from "../../../../services/action.service";
import { FileUploadService } from "../../../../services/file-upload.service";
import type { ContentBlockData } from "../../../../model/block.types";

/**
 * Inline chip for `geolocation`/`place` blocks — restored from the pre-S5
 * GeocodingHintComponent/PlacesResultHintComponent (deleted in content-blocks-FRONTEND-MIGRACION
 * §2.12), unified into one component since both kinds share `metadata.bbox`/`center`
 * and differ only in label (content-blocks-PREVIEW-INGEST-UI-IMPLEMENTACION.md §2).
 */
@Component({
  selector: "app-geo-hint-chip",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatMenu, MatMenuItem, MatMenuTrigger],
  templateUrl: "./geo-hint-chip.component.html",
  styleUrl: "./geo-hint-chip.component.scss",
})
export class GeoHintChipComponent {
  readonly data = input.required<ContentBlockData>();

  readonly #actions = inject(ActionService);

  readonly #fileUpload = inject(FileUploadService);

  protected readonly loadingView = signal(false);

  protected readonly metadata = computed(() => this.data().metadata ?? {});

  /** Guaranteed for `geolocation`, absent for `place` (content-blocks-FRONTEND-MIGRACION §1.1). */
  protected readonly formattedAddress = computed(() => this.metadata()["formatted_address"] as string | undefined);

  protected readonly label = computed(() => {
    const address = this.formattedAddress();
    if (address) return address;
    const featureCount = this.metadata()["feature_count"] as number | undefined;
    return featureCount !== undefined ? `${this.data().label} (${featureCount})` : this.data().label;
  });

  protected readonly bbox = computed(() => this.metadata()["bbox"] as [number, number, number, number] | undefined);

  protected readonly center = computed(() => this.metadata()["center"] as [number, number] | undefined);

  protected readonly canCopy = computed(() => !!this.formattedAddress());

  protected zoomTo(): void {
    const bbox = this.bbox();
    if (!bbox) return;
    this.#actions.execute("zoom_to", { bounds: bbox });
  }

  protected moveTo(): void {
    const center = this.center();
    if (!center) return;
    const zoom = this.metadata()["zoom_hint"] as number | undefined;
    this.#actions.execute("move_to", { point: { lng: center[0], lat: center[1], zoom } });
  }

  protected copy(): void {
    const address = this.formattedAddress();
    if (!address) return;
    this.#actions.execute("copy", { textContent: address });
  }

  protected async viewData(): Promise<void> {
    if (this.loadingView()) return;
    const { value, ref } = this.data();
    if (value !== null) {
      this.#actions.execute("view_data", { dataResult: { type: "geojson", geojson: value } });
      return;
    }
    if (!ref) return;
    this.loadingView.set(true);
    try {
      const result = await firstValueFrom(
        this.#fileUpload.fetchFileContent(ref.url, ref.mime_type, { filename: ref.filename, size_bytes: ref.size_bytes }),
      );
      this.#actions.execute("view_data", { dataResult: result });
    } catch (err) {
      console.error("GeoHintChipComponent: view_data fetch failed", err);
    } finally {
      this.loadingView.set(false);
    }
  }
}
